"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { API_BASE, PubPost } from "../_lib/api";
import { PostRow } from "./PostRow";
import { useFeedVotes } from "./PublicFeedVote";

// Continues a server-rendered thread list in place, the way the CRM feed does:
// the next page is fetched when the sentinel scrolls into view (or on tap)
// and appended below, so the page never reloads or jumps to the top. The
// server still renders page one for crawlers; deeper pages are fetched from
// the public API with the same sort/room/cursor.
type Page = { posts: PubPost[]; hasMore: boolean; nextCursor: string | null };

export function InfiniteFeed({
  sort,
  category,
  initialCursor,
  seenIds,
  showCategory = true,
}: {
  sort: string;
  category?: string;
  initialCursor: string | null;
  seenIds: string[];
  showCategory?: boolean;
}) {
  const [rows, setRows] = useState<PubPost[]>([]);
  const [cursor, setCursor] = useState<string | null>(initialCursor);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const seen = useRef(new Set(seenIds));
  const feedVotes = useFeedVotes();
  const sentinel = useRef<HTMLDivElement>(null);

  const loadMore = useCallback(async () => {
    if (!cursor || busy) return;
    setBusy(true);
    setFailed(false);
    try {
      const params = new URLSearchParams({ sort, cursor, limit: "25" });
      if (category) params.set("category", category);
      const res = await fetch(`${API_BASE}/api/v1/public/forum/posts?${params.toString()}`, { headers: { Accept: "application/json" } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      const page: Page = json?.data ?? json;
      const fresh = (page.posts || []).filter((p) => !seen.current.has(p.id));
      fresh.forEach((p) => seen.current.add(p.id));
      setRows((r) => [...r, ...fresh]);
      feedVotes?.register(fresh.map((p) => p.id));
      setCursor(page.hasMore ? page.nextCursor : null);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  }, [cursor, busy, sort, category, feedVotes]);

  // Auto-load when the sentinel comes into view (same trigger as the CRM feed).
  useEffect(() => {
    const el = sentinel.current;
    if (!el || !cursor) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) loadMore();
      },
      { rootMargin: "600px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [cursor, loadMore]);

  const href = cursor ? `${category ? `/c/${category}` : "/"}?sort=${sort}&cursor=${encodeURIComponent(cursor)}` : undefined;

  return (
    <>
      {rows.length > 0 && (
        <ol className="divide-y divide-slate-200 border-t border-slate-200">
          {rows.map((p) => (
            <PostRow key={p.id} post={p} showCategory={showCategory} />
          ))}
        </ol>
      )}
      <div ref={sentinel} className="flex items-center justify-center border-t border-slate-200 px-4 py-3">
        {cursor ? (
          <a
            href={href}
            rel="nofollow"
            onClick={(e) => {
              e.preventDefault();
              loadMore();
            }}
            aria-busy={busy}
            className="fp-inline inline-flex h-9 items-center rounded-md border border-slate-300 bg-white px-4 text-[13px] font-semibold text-slate-700 transition-colors hover:border-slate-400 hover:text-slate-900"
          >
            {busy ? "Loading…" : failed ? "Couldn't load more. Try again" : "More threads"}
          </a>
        ) : (
          <p className="fp-mono text-[11px] uppercase tracking-[0.14em] text-slate-400">You're all caught up</p>
        )}
      </div>
    </>
  );
}
